import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Trash2, Loader2, Search, ChevronLeft, ChevronRight, KeyRound, Wand2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { generatePassword } from '@/lib/password';
import { useWorkspaceId, useSubscriptionActive, usePermissions } from '@/contexts/WorkspaceContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import SubscribeDialog from '@/components/SubscribeDialog';

interface User {
  id: string;
  email: string;
  name: string;
  workspaceRole: string;
  createdAt: string;
  lastLoginAt: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function Users() {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const subscriptionActive = useSubscriptionActive();
  const { can } = usePermissions();
  const { data: me } = useCurrentUser();
  const myId = me?.user?.id;
  const [subNudgeOpen, setSubNudgeOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('viewer');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Search & pagination state
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Reset password state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Delete user state
  const [selectedForDelete, setSelectedForDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Role change state — tracks the member whose role is mid-update.
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (search) params.set('search', search);
      if (roleFilter && roleFilter !== 'all') params.set('role', roleFilter);

      const data = await apiFetch<{ users: User[]; pagination: Pagination }>(`/workspaces/${workspaceId}/users?${params}`);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, workspaceId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  const handleGeneratePassword = () => {
    const generated = generatePassword();
    setPassword(generated);
    toast.success('Strong password generated');
  };

  const handleCopyPassword = async () => {
    if (!password) {
      toast.error('Generate or enter a password first');
      return;
    }
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Failed to copy password');
    }
  };

  const handleInvite = async () => {
    if (!email || !name || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setCreating(true);
      const res = await apiFetch<{ emailSent?: boolean }>(`/workspaces/${workspaceId}/users`, {
        method: 'POST',
        body: JSON.stringify({ email, name, password, role }),
      });

      if (res?.emailSent === false) {
        toast.error(`${name} was added, but the invitation email could not be sent — check the server email configuration.`);
      } else {
        toast.success(`User ${name} created successfully`);
      }
      setEmail('');
      setName('');
      setPassword('');
      setRole('viewer');
      setOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetUser || !resetPassword) {
      toast.error('Please enter a new password');
      return;
    }
    setResetting(true);
    try {
      await apiFetch(`/auth/users/${resetUser.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword: resetPassword }),
      });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetOpen(false);
      setResetUser(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleRoleChange = async (user: User, newRole: string) => {
    if (newRole === user.workspaceRole) return;
    setUpdatingRoleId(user.id);
    // Optimistically reflect the new role; revert on failure.
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, workspaceRole: newRole } : u)));
    try {
      await apiFetch(`/workspaces/${workspaceId}/users/${user.id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      toast.success(`${user.name}'s role updated to ${t(`roles.${newRole}`)}`);
    } catch (error) {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, workspaceRole: user.workspaceRole } : u)));
      toast.error(error instanceof Error ? error.message : 'Failed to update role');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedForDelete) return;
    setDeleting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/users/${selectedForDelete.id}`, { method: 'DELETE' });
      toast.success(t('users.deleteSuccess'));
      if (users.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        fetchUsers();
      }
      setSelectedForDelete(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-1 p-4 md:p-8">
        <div className="container mx-auto max-w-6xl">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">{t('users.title')}</h1>
              <p className="text-muted-foreground">{t('users.subtitle')}</p>
            </div>

            <Button onClick={() => (subscriptionActive ? setOpen(true) : setSubNudgeOpen(true))}>
              <UserPlus className="h-4 w-4 mr-2" />
              {t('users.invite')}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>
                    Create a new user account with email and password
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-name">Name</Label>
                    <Input
                      id="invite-name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">{t('users.email')}</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="user@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-password">Password</Label>
                    <Input
                      id="invite-password"
                      type="text"
                      placeholder="Min 8 chars, upper+lower+number+special"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={handleGeneratePassword}
                      >
                        <Wand2 className="h-4 w-4 mr-2" />
                        Generate strong password
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCopyPassword}
                        disabled={!password}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-role">{t('users.role')}</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">{t('roles.admin')}</SelectItem>
                        <SelectItem value="finance">{t('roles.finance')}</SelectItem>
                        <SelectItem value="content">{t('roles.content')}</SelectItem>
                        <SelectItem value="media">{t('roles.media')}</SelectItem>
                        <SelectItem value="viewer">{t('roles.viewer')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleInvite} className="w-full" disabled={creating}>
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create User'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="content">Content</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>{t('users.email')}</TableHead>
                  <TableHead>{t('users.role')}</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">{t('users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      {search || roleFilter !== 'all' ? 'No users match your search' : 'No users found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {can('members:update_role') && user.workspaceRole !== 'owner' ? (
                          <Select
                            value={user.workspaceRole}
                            onValueChange={(v) => handleRoleChange(user, v)}
                            disabled={updatingRoleId === user.id}
                          >
                            <SelectTrigger className="w-[140px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">{t('roles.admin')}</SelectItem>
                              <SelectItem value="finance">{t('roles.finance')}</SelectItem>
                              <SelectItem value="content">{t('roles.content')}</SelectItem>
                              <SelectItem value="media">{t('roles.media')}</SelectItem>
                              <SelectItem value="viewer">{t('roles.viewer')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="secondary">{user.workspaceRole}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Reset password"
                          onClick={() => { setResetUser(user); setResetOpen(true); }}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {can('members:remove') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={user.id === myId || user.workspaceRole === 'owner'}
                            title={
                              user.id === myId
                                ? t('users.cannotDeleteSelf')
                                : user.workspaceRole === 'owner'
                                  ? t('users.cannotDeleteOwner')
                                  : t('users.delete')
                            }
                            onClick={() => setSelectedForDelete(user)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm px-2">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Reset Password Dialog */}
        <Dialog open={resetOpen} onOpenChange={setResetOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {resetUser?.name} ({resetUser?.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-password">New Password</Label>
                <Input
                  id="reset-password"
                  type="password"
                  placeholder="Min 8 chars, upper+lower+number+special"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                />
              </div>
              <Button onClick={handleResetPassword} className="w-full" disabled={resetting}>
                {resetting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting...</>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation */}
        <AlertDialog
          open={!!selectedForDelete}
          onOpenChange={(o) => { if (!o) setSelectedForDelete(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('users.deleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedForDelete && !selectedForDelete.lastLoginAt
                  ? t('users.deleteConfirmPending', { email: selectedForDelete.email })
                  : t('users.deleteConfirmActive', { name: selectedForDelete?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>{t('users.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => { e.preventDefault(); handleDelete(); }}
                disabled={deleting}
              >
                {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('users.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Subscription nudge for the invite action when the workspace isn't subscribed */}
        <SubscribeDialog
          open={subNudgeOpen}
          onOpenChange={setSubNudgeOpen}
          message="Inviting team members requires an active subscription."
        />
      </main>
    </div>
  );
}
